import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AlertTriangle, CloudOff } from 'lucide-react'
import type { ReactElement } from 'react'

import {
  AlertsBanner,
  KPIStrip,
  QuickActions,
  RecentActivity,
  RevenueByPlan,
  StorageUsage,
  SubscriptionsByPlan,
  dashboardQuery,
  type ActivityEntry,
} from '~/features/overview'

/**
 * `/admin` — Overview landing.
 *
 * Composes the page body that lands directly below the 56px navbar:
 *   1. KPI strip (8 tiles)
 *   2. Alerts band (4 parallel tiles)
 *   3. Quick Actions (6-up grid)
 *   4. Recent Activity card
 *
 * Wires `dashboardQuery()` via non-suspense `useQuery` — the route does not
 * sit under a Suspense boundary today (see `apps/admin-dashboard/src/providers.tsx`),
 * so we render skeletons inline instead. When the auth/contract loader work
 * lands a Suspense boundary we can swap to `useSuspenseQuery`.
 */
export const Route = createFileRoute('/admin/')({
  component: AdminOverview,
})

function AdminOverview(): ReactElement {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery(
    dashboardQuery(),
  )

  // Recent admin activity feed (`admin_activity_log`) and the unverified /
  // orphaned / security counters live behind separate queries that aren't
  // wired yet. Pass empty arrays / zeros so the placeholder states render
  // and a future wiring change is a localized prop swap.
  const activityEntries: ReadonlyArray<ActivityEntry> = []

  return (
    <div className="ka-page ka-overview-page">
      {isError ? (
        <ErrorBanner
          message={errorMessage(error)}
          onRetry={() => {
            void refetch()
          }}
        />
      ) : null}

      <div className="ka-page-section">
        <KPIStrip metrics={data} isLoading={isLoading} />
      </div>

      <div className="ka-page-section">
        <AlertsBanner metrics={data} />
      </div>

      <div className="ka-grid ka-grid-2 ka-page-section">
        <div className="ka-stack">
          <SubscriptionsByPlan metrics={data} isLoading={isLoading} />
          <RevenueByPlan metrics={data} isLoading={isLoading} />
        </div>
        <div className="ka-stack">
          <RecentActivity entries={activityEntries} isLoading={isLoading} />
          <StorageUsage metrics={data} isLoading={isLoading} />
        </div>
      </div>

      <div className="ka-page-section">
        <QuickActions metrics={data} />
      </div>

      {isFetching && !isLoading ? (
        <span
          className="ka-page-strip"
          role="status"
          aria-live="polite"
        >
          <CloudOff size={14} aria-hidden="true" />
          Refreshing platform metrics&hellip;
        </span>
      ) : null}
    </div>
  )
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return 'Couldn’t load platform metrics.'
}

interface ErrorBannerProps {
  readonly message: string
  readonly onRetry: () => void
}

function ErrorBanner({ message, onRetry }: ErrorBannerProps): ReactElement {
  return (
    <div
      className="ka-page-strip is-warning ka-page-section"
      role="alert"
    >
      <AlertTriangle size={14} aria-hidden="true" />
      <span className="ka-flex-spacer">{message}</span>
      <button
        type="button"
        className="ka-btn ka-btn--ghost ka-btn--sm"
        onClick={onRetry}
      >
        Retry
      </button>
    </div>
  )
}
